import { useGT } from 'gt-react';
import type {
  WorkbookValidation,
  WorkbookValidationKind,
  WorkbookValidationSeverity,
} from '@erp/shared-types';
import { formulaText } from '@erp/workbooks';
import { useOtherSheets } from './workbookSheetChoicesContext';

/** Validations in the user's language: kind names, severities and each rule as a phrase. */
export function useValidationText() {
  const gt = useGT();
  const sheets = useOtherSheets('');
  const sheetName = (sheetId: string) =>
    sheets.find((sheet) => sheet.id === sheetId)?.name ??
    gt('a removed worksheet', { context: 'worksheet validation rule' });
  const kinds: Record<WorkbookValidationKind, string> = {
    'not-empty': gt('Not empty', { context: 'worksheet validation kind' }),
    unique: gt('Unique', { context: 'worksheet validation kind' }),
    'accepted-values': gt('One of these values', {
      context: 'worksheet validation kind',
    }),
    range: gt('Between', { context: 'worksheet validation kind' }),
    pattern: gt('Matches a pattern', { context: 'worksheet validation kind' }),
    'no-errors': gt('No errors', { context: 'worksheet validation kind' }),
    formula: gt('Formula is TRUE', { context: 'worksheet validation kind' }),
    'in-sheet': gt('In another sheet', {
      context: 'worksheet validation kind',
    }),
  };
  const severities: Record<WorkbookValidationSeverity, string> = {
    error: gt('Error', { context: 'worksheet validation severity' }),
    warning: gt('Warning', { context: 'worksheet validation severity' }),
  };
  function range(
    validation: Extract<WorkbookValidation, { kind: 'range' }>,
  ): string {
    const { min, max } = validation;
    if (min && max)
      return gt('Between {min} and {max}', {
        context: 'worksheet validation rule, as a phrase',
        min,
        max,
      });
    return min
      ? gt('At least {min}', {
          context: 'worksheet validation rule, as a phrase',
          min,
        })
      : gt('At most {max}', {
          context: 'worksheet validation rule, as a phrase',
          max: max ?? '',
        });
  }
  function inSheet(
    validation: Extract<WorkbookValidation, { kind: 'in-sheet' }>,
  ): string {
    const sheet = sheetName(validation.sheetId);
    const column = validation.matchColumn;
    return validation.exactlyOnce
      ? gt('Exactly once in {sheet} ({column})', {
          context: 'worksheet validation rule, as a phrase',
          sheet,
          column,
        })
      : gt('In {sheet} ({column})', {
          context: 'worksheet validation rule, as a phrase',
          sheet,
          column,
        });
  }
  function describe(validation: WorkbookValidation): string {
    switch (validation.kind) {
      case 'accepted-values':
        return gt('One of {values}', {
          context: 'worksheet validation rule, as a phrase',
          values: validation.values.join(', '),
        });
      case 'range':
        return range(validation);
      case 'pattern':
        return gt('Matches {pattern}', {
          context: 'worksheet validation rule, as a phrase',
          pattern: validation.pattern,
        });
      case 'formula':
        return gt('{formula} is TRUE', {
          context: 'worksheet validation rule, as a phrase',
          formula: formulaText(validation.expression),
        });
      case 'in-sheet':
        return inSheet(validation);
      default:
        return kinds[validation.kind];
    }
  }
  return {
    kind: (kind: WorkbookValidationKind) => kinds[kind],
    severity: (severity: WorkbookValidationSeverity) => severities[severity],
    describe,
    /** "Code: Unique" — the rule with the column it checks. */
    named: (validation: WorkbookValidation) =>
      gt('{column}: {rule}', {
        context: 'worksheet validation rule with its column',
        column: validation.column,
        rule: describe(validation),
      }),
  };
}
